import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Database, Sparkle } from "@phosphor-icons/react";
import { searchFoods, api, getKidsConfig, NUTRIENT_LABELS, NUTRIENT_UNITS, MACRO_KEYS, VITAMIN_KEYS, MINERAL_KEYS } from "../lib/api";

// Category-driven gradient palette for food-image tiles.
const CAT_GRADIENT = {
  protein:   "from-red-500 to-orange-500",
  grain:     "from-amber-400 to-yellow-500",
  vegetable: "from-emerald-500 to-green-600",
  fruit:     "from-pink-400 to-fuchsia-500",
  dairy:     "from-sky-300 to-blue-400",
  nut:       "from-orange-400 to-amber-600",
  legume:    "from-lime-500 to-emerald-500",
  fat:       "from-yellow-400 to-orange-400",
  seafood:   "from-cyan-400 to-blue-500",
  sweet:     "from-rose-400 to-pink-600",
  other:     "from-slate-500 to-slate-700",
};

function FoodTile({ emoji, name, category, size = "sm" }) {
  const grad = CAT_GRADIENT[category] || CAT_GRADIENT.other;
  const box = size === "lg" ? "h-24 w-24 text-6xl" : "h-14 w-14 text-3xl";
  return (
    <div
      className={`${box} rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center shadow-sm shrink-0 select-none`}
      role="img"
      aria-label={name}
      data-testid={`food-tile-${name.replace(/\s+/g, "-")}`}
    >
      <span className="drop-shadow-sm">{emoji || "🍽️"}</span>
    </div>
  );
}

function emojiFor(name, foodEmoji) {
  const low = (name || "").toLowerCase();
  if (foodEmoji?.[low]) return foodEmoji[low];
  for (const [k, v] of Object.entries(foodEmoji || {})) {
    if (low.includes(k) || k.includes(low)) return v;
  }
  return "🍽️";
}

export default function FoodSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [foodEmoji, setFoodEmoji] = useState({});

  useEffect(() => { (async () => {
    try { const kc = await getKidsConfig(); setFoodEmoji(kc.food_emoji || {}); } catch { /* ignore */ }
  })(); }, []);

  const load = useCallback(async (query = "") => {
    try { const d = await searchFoods(query); setResults(d.results || []); }
    catch (e) { toast.error("Search failed"); }
  }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q, load]);

  const aiLookup = async () => {
    if (!q.trim()) return;
    setAiLoading(true);
    try {
      const r = await api.get("/foods/lookup", { params: { name: q } });
      setSelected({ name: r.data.name, nutrients: r.data.nutrients, source: r.data.source, category: r.data.category || "other" });
      toast.success(`Fetched via ${r.data.source}`);
    } catch (e) { toast.error("AI lookup failed"); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="food-db-page">
      <div className="pb-6 border-b border-border">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Reference database</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Food database</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">Look up any edible item. Curated entries are backed by USDA / FAO / CIQUAL. Anything missing? Ask the AI for a research-backed estimate.</p>
      </div>

      <div className="border border-border flex items-stretch rounded-2xl overflow-hidden">
        <div className="w-11 flex items-center justify-center border-r border-border">
          <MagnifyingGlass size={16} className="text-muted-foreground" />
        </div>
        <input
          value={q} onChange={e => setQ(e.target.value)}
          data-testid="food-search-input"
          placeholder="Search: chicken breast, spinach, quinoa, dark chocolate…"
          className="flex-1 h-12 px-4 bg-transparent focus:outline-none font-mono text-sm"
        />
        <button onClick={aiLookup} disabled={aiLoading || !q.trim()} data-testid="ai-lookup-btn"
          className="h-12 px-5 border-l border-border bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2 transition-colors duration-200">
          <Sparkle size={14} /> {aiLoading ? "Asking AI…" : "AI Lookup"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-px bg-border border border-border rounded-2xl overflow-hidden">
        <div className="bg-card max-h-[640px] overflow-y-auto" data-testid="food-results">
          {results.length === 0 ? (
            <div className="p-6 font-mono text-sm text-muted-foreground">No matches. Try AI lookup.</div>
          ) : results.map((r) => (
            <button key={r.name} onClick={() => setSelected({ ...r, source: "db" })}
              data-testid={`food-item-${r.name.replace(/\s+/g, "-")}`}
              className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted transition-colors duration-150 flex items-center gap-3 ${selected?.name === r.name ? "bg-muted" : ""}`}>
              <FoodTile emoji={emojiFor(r.name, foodEmoji)} name={r.name} category={r.category} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize truncate">{r.name}</span>
                  <span className="font-mono text-xs text-muted-foreground shrink-0 ml-2">{Math.round(r.nutrients.calories)} kcal</span>
                </div>
                <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                  P {r.nutrients.protein}g · C {r.nutrients.carbs}g · F {r.nutrients.fat}g
                </div>
                {r.category && (
                  <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground mt-0.5">{r.category}</div>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="bg-card p-6" data-testid="food-detail">
          {!selected ? (
            <div className="h-full flex items-center justify-center font-mono text-sm text-muted-foreground">Select an item to view its full nutrient profile.</div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <FoodTile emoji={emojiFor(selected.name, foodEmoji)} name={selected.name} category={selected.category} size="lg" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Per 100g edible portion</div>
                    <h2 className="font-display font-bold text-3xl tracking-tight mt-1 capitalize">{selected.name}</h2>
                    {selected.category && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{selected.category}</div>}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border rounded-full ${selected.source === "db" ? "border-accent/60 text-accent" : "border-primary/60 text-primary"}`}>
                  {selected.source === "db" ? <Database size={12} /> : <Sparkle size={12} />} {selected.source}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden">
                {[...MACRO_KEYS, ...VITAMIN_KEYS, ...MINERAL_KEYS].map(k => (
                  <div key={k} className="p-3 bg-card">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{NUTRIENT_LABELS[k]}</div>
                    <div className="font-mono text-lg mt-1">{(selected.nutrients?.[k] ?? 0)}<span className="text-xs text-muted-foreground ml-1">{NUTRIENT_UNITS[k]}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
